import errorMonitor from '@/infra/tracking/error-monitor'
import { Spinner } from '@/presentation/components'
import GenericError from '@/presentation/components/ui/feedback/generic-error'
import WhatsAppButton from '@/presentation/components/ui/button/whatsapp-button'
import AppProviders from '@/presentation/contexts/app-providers'
import useAccount from '@/presentation/hooks/auth/use-account'
import RootLayout from '@/presentation/components/ui/layout/root-layout'
import { createBrowserRouter, RouterProvider, RouteObject } from 'react-router-dom'
import { AuthenticatedRoutes } from './authenticated'
import { UnauthenticatedRoutes } from './unauthenticated'
import React from 'react'

// ============================================
// ROUTER PROVIDER COMPONENT
// ============================================

/**
 * Creates and provides the router instance
 * Memoized to avoid recreating the router on every render
 */
function RouterProviderComponent({ routes }: { routes: RouteObject[] }) {
  const router = React.useMemo(() => {
    const wrappedCreateBrowserRouter = errorMonitor.wrapCreateBrowserRouter(createBrowserRouter)

    return wrappedCreateBrowserRouter(
      [
        {
          element: <RootLayout />,
          errorElement: <Spinner fullScreen />,
          children: routes
        }
      ],
      {
        // Enable React Router v7 features
        future: {
          v7_normalizeFormMethod: true
        }
      }
    )
  }, [routes])

  return <RouterProvider router={router} fallbackElement={<Spinner fullScreen />} />
}

// ============================================
// ROUTER FACTORY
// ============================================

function RouterFactory(): JSX.Element {
  const [account] = useAccount()
  // Wait for account to load
  if (!account) {
    console.log('[RouterFactory] ⏳ Waiting for account...')
    return <Spinner fullScreen />
  }

  console.log('[RouterFactory] ✅ Account loaded')

  // Combine all routes
  const routes = [
    {
      children: UnauthenticatedRoutes(),
      errorElement: <Spinner fullScreen />
    },
    {
      children: AuthenticatedRoutes(account),
      errorElement: <GenericError fullScreen />
    },
    {
      path: '*',
      element: <GenericError fullScreen />
    }
  ]

  return <RouterProviderComponent routes={routes} />
}

// ============================================
// MAIN ROUTER COMPONENT
// ============================================

export default function Router() {
  return (
    <AppProviders>
      <RouterFactory />
      <WhatsAppButton />
    </AppProviders>
  )
}
